import React from 'react';
import { useTranslation } from 'react-i18next';
import { IProject, IProjectPhase, PhaseType } from '@/types/project';
import { LinkedProduct } from '@/services/bomService';
import PreBOMPhasePanel from './phases/PreBOMPhasePanel';

interface PhaseDetailPanelProps {
  project: IProject;
  currentPhase?: IProjectPhase;
  linkedProduct?: LinkedProduct | null;
  onCreateProduct?: () => void;
  onLinkProduct?: () => void;
  onRefresh?: () => void;
}

const PhaseDetailPanel: React.FC<PhaseDetailPanelProps> = ({
  project,
  currentPhase,
  linkedProduct,
  onCreateProduct,
  onLinkProduct,
  onRefresh,
}) => {
  const { t } = useTranslation('project');

  if (!currentPhase) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
        <p className="text-gray-500 dark:text-gray-400 text-center">
          {t('phases.noActivePhase')}
        </p>
      </div>
    );
  }

  // Route to appropriate phase panel based on phase type
  const phaseType = currentPhase.phase_type as PhaseType;

  switch (phaseType) {
    case 'pre_bom':
      return (
        <PreBOMPhasePanel
          project={project}
          phase={currentPhase}
          linkedProduct={linkedProduct}
          onCreateProduct={onCreateProduct}
          onLinkProduct={onLinkProduct}
          onRefresh={onRefresh}
        />
      );

    case 'evt':
    case 'dvt':
    case 'pvt':
      // Build phase panel (EVT/DVT/PVT)
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {phaseType.toUpperCase()} {t('phases.phaseActions')}
            </h3>
            <span className="px-3 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
              {phaseType.toUpperCase()}
            </span>
          </div>

          <div className="space-y-4">
            {/* Phase Readiness Checklist */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('phases.readinessChecklist')}
              </label>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <input type="checkbox" className="rounded text-emerald-600" disabled />
                    <span>{t('phases.checklistCriticalParts')}</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <input type="checkbox" className="rounded text-emerald-600" disabled />
                    <span>{t('phases.checklistNoTbd')}</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <input type="checkbox" className="rounded text-emerald-600" disabled />
                    <span>{t('phases.checklistEngineerSignoff')}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Build Tracking */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('phases.builds')}
              </label>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-center text-gray-500 dark:text-gray-400">
                {t('phases.noBuilds')}
              </div>
            </div>

            {/* Transition Button */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled
              >
                {t('phases.transitionToNext', { phase: phaseType === 'evt' ? 'DVT' : phaseType === 'dvt' ? 'PVT' : 'MP' })}
              </button>
            </div>
          </div>
        </div>
      );

    case 'mp':
      // MP (Mass Production) phase panel
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('phases.mpPhaseActions')}
            </h3>
            <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
              MP
            </span>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="font-medium text-green-800 dark:text-green-200">
                  {t('phases.mpStatus')}
                </p>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {t('phases.mpDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>
      );

    default:
      // Generic phase panel for other phases (concept, feasibility, etc.)
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {currentPhase.phase_name}
            </h3>
            <span className="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
              {currentPhase.phase_type.replace('_', ' ').toUpperCase()}
            </span>
          </div>

          <div className="text-gray-500 dark:text-gray-400">
            <p>{t('phases.genericPhaseDescription')}</p>
          </div>
        </div>
      );
  }
};

export default PhaseDetailPanel;
